import type { FormEvent } from 'react';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { isModernSubscription } from '@gonfalon/billing/subscriptions';
import { isSandbox } from '@gonfalon/constants/constants';
import { trackCreateSupportTicket } from '@gonfalon/contact-support/trackCreateSupportTicket';
import { useTypedAppForm } from '@gonfalon/forms/hooks';
import { MarketoForm } from '@gonfalon/marketing-forms/forms';
import { useMarketingFormSubmit } from '@gonfalon/marketing-forms/useMarketingFormSubmit';
import { trackContactSalesFormSubmitted } from '@gonfalon/marketo/analytics';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { checkAccessFromDenials } from '@gonfalon/permissions/access';
import { NewTabLink } from '@gonfalon/ui/NewTabLink';
import { Alert, Button, Form } from '@launchpad-ui/components';
import { z } from 'zod';

import type { ContactSalesFormProps } from './types';
import { SANDBOX_PRODUCT_INTEREST, SUPPORT_TICKET_URL } from './types';

import styles from './ContactSalesFormFields.module.css';

const FormSchema = z.object({
  email: z.email({ error: () => msg('billing.contactSalesForm.emailInvalid') }),
  company: z.string().min(1, { error: () => msg('billing.contactSalesForm.companyRequired') }),
  firstName: z.string().min(1, { error: () => msg('billing.contactSalesForm.firstNameRequired') }),
  lastName: z.string().min(1, { error: () => msg('billing.contactSalesForm.lastNameRequired') }),
  moreDetails: z.string().optional(),
});

type FormData = z.infer<typeof FormSchema>;

/**
 * Contact sales, submitting through the backend form endpoint. Replaces the
 * Marketo-rendered form: the fields below are the ones Marketo form 1941
 * renders today, plus the hidden fields the old form set programmatically.
 */
export function ContactSalesFormFields({
  requestedFeature,
  headerText,
  productInterest,
  onFormSubmit,
}: ContactSalesFormProps) {
  const { profile, account, subscription } = useAccountContext();
  // The member endpoint requires the same subscription-management access the
  // billing surfaces do, but this form also renders where any member can reach
  // it — upsells, approvals, CLI auth, the learning center, experiments.
  // Members without that access would get a 403, so they submit as visitors
  // instead: no account permissions, at the cost of a captcha.
  const canManageSubscription = checkAccessFromDenials(account._access, 'updateSubscription').isAllowed;

  const { submit, isPending, isError, isSuccess, captcha } = useMarketingFormSubmit({
    formId: MarketoForm.ContactSales,
    audience: canManageSubscription ? 'member' : 'visitor',
  });

  // Sandbox runs on a shared demo account, so its profile is not the visitor's.
  const inSandbox = isSandbox();

  const onSubmit = ({ value }: { value: FormData }) => {
    submit(
      // Keys must match the marketo-form-submission-config allow-list for this
      // form ID, which uses Marketo's own lead field names.
      {
        email: value.email,
        firstName: value.firstName,
        lastName: value.lastName,
        company: value.company,
        Description__c: value.moreDetails,
        requestedfeature: requestedFeature,
        Website_Dr_Button__c: inSandbox ? SANDBOX_PRODUCT_INTEREST : productInterest,
        ...(isModernSubscription(subscription)
          ? { planType: subscription.planType, subscriptionState: subscription.state }
          : {}),
      },
      {
        onSuccess: () => {
          trackContactSalesFormSubmitted({ url: window.location.href, path: window.location.pathname });
          onFormSubmit?.();
        },
      },
    );
  };

  const form = useTypedAppForm<FormData>({
    defaultValues: {
      email: inSandbox ? '' : profile?.email || '',
      company: inSandbox ? '' : account?.organization || '',
      firstName: inSandbox ? '' : profile?.firstName || '',
      lastName: inSandbox ? '' : profile?.lastName || '',
      moreDetails: '',
    },
    validators: {
      onChange: FormSchema,
      onSubmit: FormSchema,
      onMount: FormSchema,
    },
    onSubmit,
  });

  const { AppForm, AppField, Subscribe } = form;

  if (isSuccess) {
    return (
      <div className={styles.success} data-test-id="contact-sales-success">
        <div>
          <h2 className={styles.successTitle}>
            <Message id="billing.contactSalesForm.thankYouHeading" />
          </h2>
          <p className={styles.successLead}>
            <Message id="billing.contactSalesForm.thankYouBody" />
          </p>
          <p>
            <Message id="billing.contactSalesForm.responseTime" />
          </p>
        </div>
        <p>
          <Message
            id="billing.contactSalesForm.learnMoreBody"
            values={{
              strong: (chunks) => <span className={styles.bold}>{chunks}</span>,
              pricing: () => (
                <NewTabLink
                  className={styles.link}
                  href="https://launchdarkly.com/pricing/"
                  text={msg('billing.contactSalesForm.pricingLink')}
                />
              ),
            }}
          />
        </p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{headerText ?? msg('billing.contactSalesForm.defaultHeading')}</h2>
      <p className={styles.supportPrompt}>
        <Message
          id="billing.contactSalesForm.supportPrompt"
          values={{
            ticket: () => (
              <NewTabLink
                className={styles.link}
                href={SUPPORT_TICKET_URL}
                text={msg('billing.contactSalesForm.supportTicketLink')}
                showIcon={false}
                onClick={() => trackCreateSupportTicket('ContactUsForm')}
              />
            ),
          }}
        />
      </p>
      <Form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <AppForm>
          {isError && <Alert status="error">{msg('billing.contactSalesForm.submitError')}</Alert>}
          <div className={styles.fields}>
            <AppField name="email">
              {(field) => (
                <field.TextInputField label={msg('billing.contactSalesForm.emailLabel')} type="email" isRequired />
              )}
            </AppField>
            <AppField name="company">
              {(field) => <field.TextInputField label={msg('billing.contactSalesForm.companyLabel')} isRequired />}
            </AppField>
            <AppField name="firstName">
              {(field) => <field.TextInputField label={msg('billing.contactSalesForm.firstNameLabel')} isRequired />}
            </AppField>
            <AppField name="lastName">
              {(field) => <field.TextInputField label={msg('billing.contactSalesForm.lastNameLabel')} isRequired />}
            </AppField>
            <div className={styles.wide}>
              <AppField name="moreDetails">
                {(field) => <field.TextAreaInputField label={msg('billing.contactSalesForm.detailsLabel')} rows={4} />}
              </AppField>
            </div>
          </div>
          {captcha}
          <div className={styles.submit}>
            <Subscribe selector={(state) => state.isValid}>
              {(isValid) => (
                <Button
                  variant="primary"
                  type="submit"
                  data-test-id="contact-sales-submit"
                  isDisabled={!isValid || isPending}
                  isPending={isPending}
                >
                  {msg('billing.contactSalesForm.submit')}
                </Button>
              )}
            </Subscribe>
          </div>
        </AppForm>
      </Form>
      <p className={styles.policies}>
        <Message
          id="billing.contactSalesForm.policyBody"
          values={{
            terms: () => (
              <NewTabLink
                className={styles.link}
                href="https://launchdarkly.com/policies/subscription-terms/"
                text={msg('billing.contactSalesForm.termsLink')}
              />
            ),
            privacy: () => (
              <NewTabLink
                className={styles.link}
                href="https://launchdarkly.com/policies/privacy/"
                text={msg('billing.contactSalesForm.privacyLink')}
              />
            ),
          }}
        />
      </p>
    </div>
  );
}
